import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IDynamicViewControl, IKeyValuePairAny, IPatientHealthRecord } from '@app/core/contracts/dto';
import { DynamicFieldType } from '@app/core/enums/dynamic-field-type.enum';
import { DynamicViewService } from '@app/core/services/dynamic-view-service';
import { ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToggleInstructionComponent } from '../toggle-instruction-modal/toggle-instruction.component';

@Component({
    selector: 'dynamic-form',
    templateUrl: './dynamic-form.component.html',
    styleUrls: ['./dynamic-form.component.scss'],
    standalone: false
})
export class DynamicFormComponent implements OnChanges, OnDestroy {
  private dynamicViewService = inject(DynamicViewService);
  private modalController = inject(ModalController);

  @Input() formKey = null;
  @Input() formData: IPatientHealthRecord = null;
  @Output() dataChanged: EventEmitter<IKeyValuePairAny[]> = new EventEmitter();
  @Output() customComponentDataChanged: EventEmitter<IKeyValuePairAny> = new EventEmitter();

  errorMessage = null;

  controls: IDynamicViewControl[] = [];
  formGroupControl: IDynamicViewControl;
  dynamicForm: FormGroup;
  componentDestroyed$: Subject<boolean> = new Subject();
  formBuilt = false;
  fieldTypes = DynamicFieldType;
  nonValueTypes: DynamicFieldType[] = [DynamicFieldType.DialogToggleInstruction, DynamicFieldType.Label];

  ngOnChanges() {
    if (!this.formKey) {
      this.errorMessage = 'Key not specified.';
      return;
    }
    this.loadFormControls();
  }

  buildForm(controls: IDynamicViewControl[]) {
    if (controls.length === 0) {
      this.errorMessage = 'No controls found';
    } else {
      this.errorMessage = null;
    }

    this.dynamicForm = new FormGroup({});
    this.formGroupControl = controls.find((c) => c.key === this.formKey);
    const containedControls = controls.filter((c) => c.key !== this.formKey && this.nonValueTypes.indexOf(c.fieldType) === -1); // remove container control, dialogs/non value types

    // Only emit initial values that are non-null so we do not persist "unanswered"
    const defaultValues: IKeyValuePairAny[] = [];
    containedControls.forEach((c) => {
      const formControl = this.buildFormControl(c);
      this.dynamicForm.addControl(c.key, formControl);
      if (formControl.value !== null && formControl.value !== undefined) {
        defaultValues.push({ key: c.key, value: formControl.value });
      }
    });

    if (defaultValues.length > 0) {
      this.dataChanged.emit(defaultValues);
    }

    //

    this.setHiddenProperty();
    this.formBuilt = true;

    // Subscribe to form changes
    this.dynamicForm.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe({
      next: (changes) => {
        const newValues: IKeyValuePairAny[] = [];
        Object.entries(changes).forEach((element) => {
          const key = element[0];
          const value = element[1];
          newValues.push({ key, value });
          // If this control's value is affirmative and its parent is ToggleYesNo, set parent to true
          this.maybeSetParentYesNoToTrue(key, value);
        });
        this.dataChanged.emit(newValues);
      }
    });
  }

  setYesNo(controlKey: string, value: boolean) {
    this.dynamicForm.get(controlKey).setValue(value);
    this.setHiddenProperty();
  }

  maybeSetParentYesNoToTrue(controlKey: string, value: unknown) {
    const control = this.controls.find((c) => c.key === controlKey);
    if (!control?.parentId) return;
    const parent = this.controls.find((pc) => pc.id === control.parentId);
    if (!parent || parent.fieldType !== DynamicFieldType.ToggleYesNo) return;
    const parentControl = this.dynamicForm.get(parent.key);
    if (!parentControl) return;
    if (parentControl.value === true) return;
    const isAffirmative =
      value === true ||
      (typeof value === 'string' && value.length > 0) ||
      (Array.isArray(value) && value.length > 0);
    if (isAffirmative) {
      parentControl.setValue(true);
      this.setHiddenProperty();
    }
  }

  allYesNoAnswered(): boolean {
    if (!this.dynamicForm || !this.controls.length) return true;
    const toggleControls = this.controls.filter((c) => c.fieldType === DynamicFieldType.ToggleYesNo);
    for (const c of toggleControls) {
      const control = this.dynamicForm.get(c.key);
      if (!control || (control.value !== true && control.value !== false)) return false;
    }
    return true;
  }

  customComponentUpdated(ev: IKeyValuePairAny) {
    // not form control
    const parsed = ev.value;
    ev.value = JSON.stringify(parsed);
    this.customComponentDataChanged.emit(ev);
    // If this component has a parent ToggleYesNo and value is non-empty, set parent to Yes
    const control = this.controls.find((c) => c.key === ev.key);
    if (!control?.parentId) return;
    const parent = this.controls.find((pc) => pc.id === control.parentId);
    if (!parent || parent.fieldType !== DynamicFieldType.ToggleYesNo) return;
    const parentControl = this.dynamicForm.get(parent.key);
    if (!parentControl || parentControl.value === true) return;
    const isNonEmpty = Array.isArray(parsed) ? parsed.length > 0 : parsed != null && parsed !== '';
    if (isNonEmpty) {
      parentControl.setValue(true);
      this.setHiddenProperty();
    }
  }

  buildFormControl(control: IDynamicViewControl): FormControl {
    const formControl = new FormControl(this.getInitialValue(control), control.required ? [Validators.required] : []);
    return formControl;
  }

  setHiddenProperty() {
    this.controls.forEach((c) => {
      if (c.parentIsTrigger) {
        const parentField = this.controls.find((pc) => pc.id === c.parentId);
        const parentControl = this.dynamicForm.get(parentField.key);
        // Show children when parent is null (unanswered) or true; hide only when parent is false
        const hidden = parentControl.value === false;
        c.hidden = hidden;

        if (hidden) {
          if (c.fieldType === DynamicFieldType.ComponentMedicalProblemManager) {
            const control = this.dynamicForm.get(c.key);
            control.setValue([]);
          } else {
            this.dynamicForm.get(c.key).setValue(this.getDefaultValue(c));
          }
        }
      }
    });
  }

  getInitialValue(control: IDynamicViewControl) {
    const initField = this.formData?.value?.find((c) => c.key === control.key);
    if (initField) {
      let initValue: any = initField.value;

      switch (control.fieldType) {
        case DynamicFieldType.CheckBox:
          initValue = this.convertToBoolean(initValue);
          break;
        case DynamicFieldType.ToggleYesNo:
          initValue = initValue === null || initValue === undefined ? null : this.convertToBoolean(initValue);
          break;
        case DynamicFieldType.ComponentMedicationManager:
          initValue = JSON.parse(initValue);
          initField.value = initValue; // set value to object
          break;
        case DynamicFieldType.ComponentMedicalProblemManager:
          initValue = JSON.parse(initValue);
          initField.value = initValue; // set value to object
          break;
        case DynamicFieldType.ComponentPreviousSurgeries:
          initValue = JSON.parse(initValue);
          initField.value = initValue; // set value to object
          break;
      }

      return initValue;
    } else {
      return this.getDefaultValue(control);
    }
  }

  getDefaultValue(control: IDynamicViewControl): any {
    if (control.fieldType === this.fieldTypes.ToggleYesNo) {
      const raw = control.defaultValue;
      if (raw === null || raw === undefined) {
        return null;
      }
      try {
        return this.convertToBoolean(JSON.parse(raw));
      } catch {
        return null;
      }
    }

    let defaultValue: any = control.defaultValue;

    if (control.fieldType === this.fieldTypes.CheckBox) {
      defaultValue = this.convertToBoolean(JSON.parse(defaultValue));
    }

    if (defaultValue === null || defaultValue === undefined) {
      defaultValue = '';
    }
    return defaultValue;
  }

  getCustomControlData(key: string) {
    return this.formData.value.find((c) => c.key === key);
  }

  loadFormControls() {
    this.dynamicViewService
      .getControlsByKey(this.formKey)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          this.controls = data;
          this.buildForm(this.controls);
          this.displayFormDialogs(this.controls);
        }
      });
  }

  async displayFormDialogs(controls: IDynamicViewControl[]) {
    // display first dialog linked to form.
    // only one dialog type implemented currently, might change
    const diallogTypes: DynamicFieldType[] = [DynamicFieldType.DialogToggleInstruction];
    const dialogControl = controls.find((c) => diallogTypes.indexOf(c.fieldType) > -1);
    if (!dialogControl) {
      return;
    }

    let dialogRef = null;
    switch (dialogControl.fieldType) {
      case DynamicFieldType.DialogToggleInstruction:
        dialogRef = await this.modalController.create({
          component: ToggleInstructionComponent,
          backdropDismiss: false,
          componentProps: {
            message: dialogControl.generalDescription
          },
          cssClass: 'modal-small'
        });
        break;
    }

    await dialogRef.present();
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  convertToBoolean(val: any) {
    if (val === null || val === undefined) {
      return null;
    } else if (val === true || (typeof val === 'string' && val.toLowerCase() === 'true') || val === 1) {
      return true;
    } else if (val === false || (typeof val === 'string' && val.toLowerCase() === 'false') || val === 0) {
      return false;
    } else {
      return null;
    }
  }
}
